import axios from 'axios';
import { v2Api, API_ROOT } from './v2Client';
import { ensureAccessToken } from './authTokens';
import { clubRequestHeaders } from '../currentClub';

function normArr(data) {
  return Array.isArray(data) ? data : [];
}

async function post(path, payload) {
  const { data } = await v2Api.post(path, payload);
  return data;
}

/**
 * Heure prévue d'un match → API. Convention : Match.scheduled_at est stocké en HEURE DU CLUB (Bruxelles),
 * relu tel quel par tous les clients. On envoie donc l'heure murale saisie (composantes locales du navigateur,
 * l'admin est au club) étiquetée « Z », que l'API garde telle quelle (MatchService.UpdateScheduleAsync).
 * Remplace l'ancien « toISOString() + 2 h » fixe, juste en heure d'été seulement : à partir du passage à
 * l'heure d'hiver (UTC+1), un match reprogrammé à 19:00 était enregistré à 20:00.
 */
function scheduleIsoForApi(scheduledAtIso) {
  const d = new Date(scheduledAtIso);
  if (!Number.isFinite(d.getTime())) return scheduledAtIso;
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}:00.000Z`;
}


export const tournamentService = {
  list: (filters = {}) => post('/Tournament/list', filters).then(normArr),
  get: (id) => post('/Tournament/get', { id }),
  /**
   * GET /Tournament/{id}/bundle — fiche ERP complète en un appel : { tournament, participants, categories, pools,
   * category_brackets: {catId: bracket}, category_phase_boards: {catId: [board]}, schedule }. null si la route est
   * absente (API pas encore à jour) : l'appelant revient aux appels unitaires.
   */
  bundle: async (id) => {
    try {
      const { data } = await v2Api.get(`/Tournament/${encodeURIComponent(String(id))}/bundle`);
      return data && typeof data === 'object' && data.tournament ? data : null;
    } catch (e) {
      if (e?.response?.status === 404 || e?.response?.status === 405) return null;
      throw e;
    }
  },
  create: (payload) => post('/Tournament/create', payload),
  update: (payload) => post('/Tournament/update', payload),
  updateStatus: (id, status) => post('/Tournament/update_status', { id, status }),
  uploadPhoto: async (id, file) => {
    const fd = new FormData();
    fd.append('id', id);
    fd.append('file', file);
    const access = await ensureAccessToken(API_ROOT);
    const headers = clubRequestHeaders();
    if (access) headers.Authorization = `Bearer ${access}`;
    const { data } = await axios.post(`${API_ROOT}/Tournament/upload_photo`, fd, {
      headers,
      maxContentLength: 12 * 1024 * 1024,
      maxBodyLength: 12 * 1024 * 1024,
    });
    return data;
  },
  remove: (id) => post('/Tournament/delete', { id }),
  generatePools: (id) => post('/Tournament/generate_pools', { id }),
  generateBracket: (id) => post('/Tournament/generate_bracket', { id }),
  generateKnockoutFromPools: (id) => post('/Tournament/generate_knockout_from_pools', { id }),
  getBracket: (id, category_id) => post('/Tournament/bracket', { id, category_id: category_id ?? null }),
  swapBracketSeeds: (tournament_id, player_a_id, player_b_id) =>
    post('/Tournament/swap_bracket_seeds', { tournament_id, player_a_id, player_b_id }),
  replaceBracketPlayer: (tournament_id, outgoing_player_id, incoming_player_id) =>
    post('/Tournament/replace_bracket_player', { tournament_id, outgoing_player_id, incoming_player_id }),
  getPools: (id) => post('/Tournament/pools', { id }),
  // "Générer le tournoi" — un seul bouton : génère le tableau de chaque catégorie prête puis l'horaire
  // complet en un seul appel. deleteGenerated est son symétrique ("Tout supprimer").
  generate: (id) => post('/Tournament/generate', { id }),
  deleteGenerated: (id) => post('/Tournament/delete_generated', { id }),
  // Place les matchs sans horaire, sans jamais déplacer ceux déjà placés ; `replaceAll` = recalcul complet des matchs
  // à venir (sur demande explicite seulement).
  generateSchedule: (id, { replaceAll = false } = {}) => post('/Tournament/generate_schedule', { id, replace_all: replaceAll }),
  getSchedule: (id) => post('/Tournament/get_schedule', { id }),
  rescheduleMatch: (match_id, scheduled_at, terrain_number) =>
    post('/Tournament/reschedule_match', { match_id, scheduled_at: scheduleIsoForApi(scheduled_at), terrain_number }),
  /** Retire le match du planning (colonne « À placer »). */
  unscheduleMatch: (match_id) => post('/Tournament/reschedule_match', { match_id, unschedule: true }),

  participants: {
    list: (tournament_id) => post('/TournamentParticipant/list', { tournament_id }).then(normArr),
    register: (payload) => post('/TournamentParticipant/register', payload),
    update: (payload) => post('/TournamentParticipant/update', payload),
    assignCategory: (id, category_id) => post('/TournamentParticipant/assign_category', { id, category_id: category_id ?? null }),
    remove: (id) => post('/TournamentParticipant/remove', { id }),
    undoWithdrawal: (id) => post('/TournamentParticipant/undo_withdrawal', { id }),
  },

  categories: {
    list: (tournament_id) => post('/TournamentCategory/list', { tournament_id }).then(normArr),
    // payload: { name, match_duration_minutes, plate_count, placement_matches_enabled }
    create: (tournament_id, payload) => post('/TournamentCategory/create', { tournament_id, ...payload }),
    update: (id, payload) => post('/TournamentCategory/update', { id, ...payload }),
    remove: (id) => post('/TournamentCategory/delete', { id }),
    // Régénération/suppression d'un tableau individuel : réservé aux corrections ponctuelles après la
    // génération initiale (voir le bouton "Générer le tournoi" ci-dessus pour le flux normal).
    generateBracket: (id) => post('/TournamentCategory/generate_bracket', { id }),
    deleteBracket: (id) => post('/TournamentCategory/delete_bracket', { id }),
  },
  // Tournament matches are ordinary Match rows — score entry goes through matchesService.updateMatchScores
  // (see TournamentDetailPage.js), not a dedicated tournament endpoint.

  // Configurable tournament-format engine (see squash_manger_api_v2/Services/TournamentPhaseEngine.cs):
  // a category's ordered pipeline of Phases, wired together by PhaseConnections, replaces the old
  // implicit one-bracket-per-category format.
  phases: {
    list: (category_id) => post('/TournamentPhase/list', { category_id }).then(normArr),
    // payload: { name, phase_type ("knockout"|"round_robin"|"monrad"), role, config_json }
    create: (category_id, payload) => post('/TournamentPhase/create', { category_id, ...payload }),
    update: (id, payload) => post('/TournamentPhase/update', { id, ...payload }),
    remove: (id) => post('/TournamentPhase/delete', { id }),
    generate: (id) => post('/TournamentPhase/generate', { id }),
    getOutput: (id) => post('/TournamentPhase/output', { id }),
    preview: (id) => post('/TournamentPhase/preview', { id }),
    // Phase-by-phase live board for the "Tableau" view (rounds/pools/monrad_rounds per phase, in order) —
    // see TournamentPhaseController.Board.
    board: (category_id) => post('/TournamentPhase/board', { category_id }).then(normArr),
  },

  phaseConnections: {
    // payload: { kind ("category_rank"|"phase_output"), source_phase_id, spec, order_index }
    create: (phase_id, payload) => post('/TournamentPhase/connection/create', { phase_id, ...payload }),
    remove: (id) => post('/TournamentPhase/connection/delete', { id }),
  },
};
